import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";

// --- small date helpers (local, no external date lib) ---
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const weekRange = (d) => {
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const monday = new Date(d);
  monday.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return [fmt(monday), fmt(sunday)];
};
const monthRange = (d) => [fmt(new Date(d.getFullYear(), d.getMonth(), 1)), fmt(new Date(d.getFullYear(), d.getMonth() + 1, 0))];
const hhmm = (s) => (s ? String(s).slice(11, 16) : "");
const hrs = (n) => `${(n ?? 0).toFixed(1)}h`;

const STATE_STYLE = {
  working: { label: "Working", color: "#10B981", bg: "#ECFDF5" },
  idle: { label: "Idle", color: "#F59E0B", bg: "#FFFBEB" },
  off: { label: "Not in", color: "#6B7280", bg: "#F3F4F6" },
};

const TeamReport = () => {
  const toast = useRef(null);

  const [tab, setTab] = useState("today"); // today | week | month
  const [date, setDate] = useState(new Date());
  const [developer, setDeveloper] = useState(null);
  const [developerOptions, setDeveloperOptions] = useState([]);

  const [board, setBoard] = useState(null); // team-today
  const [summary, setSummary] = useState(null); // developer-summary
  const [loading, setLoading] = useState(false);

  const notifyError = (detail) => toast.current?.show({ severity: "error", summary: "Error", detail, life: 4000 });

  // Today board — also seeds the developer dropdown.
  const loadToday = async () => {
    setLoading(true);
    try {
      const res = await Http.get(`/tasks/team-today?date=${fmt(date)}`);
      const data = res?.data?.data;
      setBoard(data);
      if (data?.members?.length) {
        setDeveloperOptions(data.members.map((m) => ({ label: m.name, value: m.developer_id })));
        if (!developer) setDeveloper(data.members[0].developer_id);
      }
    } catch (e) {
      notifyError("Could not load the team board.");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!developer) return;
    setLoading(true);
    try {
      const [start, end] = tab === "week" ? weekRange(date) : monthRange(date);
      const res = await Http.get(`/tasks/developer-summary?developer_id=${developer}&start=${start}&end=${end}`);
      setSummary(res?.data?.data);
    } catch (e) {
      notifyError("Could not load the developer summary.");
    } finally {
      setLoading(false);
    }
  };

  // Ensure the developer dropdown is populated even if Today wasn't opened first.
  useEffect(() => {
    if (!developerOptions.length) loadToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "today") loadToday();
    else loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, date, developer]);

  // ---- renderers ----
  const renderToday = () => {
    if (!board) return null;
    const counts = board.members.reduce((a, m) => ({ ...a, [m.state]: (a[m.state] || 0) + 1 }), {});
    return (
      <div>
        <div className="d-flex flex-wrap mb-3" style={{ gap: "10px" }}>
          {["working", "idle", "off"].map((s) => (
            <div key={s} className="px-3 py-2" style={{ borderRadius: 8, background: STATE_STYLE[s].bg, color: STATE_STYLE[s].color, fontWeight: 600 }}>
              {STATE_STYLE[s].label}: {counts[s] || 0}
            </div>
          ))}
        </div>
        <div className="d-flex flex-wrap" style={{ gap: "12px" }}>
          {board.members.map((m) => {
            const st = STATE_STYLE[m.state];
            return (
              <div key={m.developer_id} className="p-3" style={{ width: 260, borderRadius: 10, border: "1px solid #eee", borderLeft: `4px solid ${st.color}` }}>
                <div className="d-flex justify-content-between align-items-center">
                  <strong>{m.name}</strong>
                  <span style={{ fontSize: 12, color: st.color, fontWeight: 700 }}>{st.label}</span>
                </div>
                <div style={{ fontSize: 13, color: "#666", marginTop: 6 }}>{m.check_in ? `In since ${hhmm(m.check_in)}` : "Not checked in"}</div>
                <div style={{ fontSize: 13, marginTop: 8, minHeight: 34 }}>
                  {m.active_task ? (
                    <>
                      <div style={{ fontWeight: 600 }} className="text-truncate" title={m.active_task.title}>
                        #{m.active_task.id} {m.active_task.title}
                      </div>
                      <div style={{ color: "#888", fontSize: 12 }}>since {hhmm(m.active_task.since)}</div>
                    </>
                  ) : (
                    <span style={{ color: "#bbb" }}>No active task</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBars = () => {
    if (!summary) return null;
    const max = Math.max(1, ...summary.days.map((d) => Math.max(d.in_office_hours, d.worked_hours)));
    return (
      <div style={{ marginTop: 8 }}>
        {summary.days.map((d) => (
          <div key={d.date} className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
            <div style={{ width: 96, fontSize: 12, color: "#666" }}>{d.date}</div>
            <div style={{ flex: 1 }}>
              <div style={{ position: "relative", height: 22, background: "#F3F4F6", borderRadius: 4 }} title={`In office ${hrs(d.in_office_hours)}`}>
                <div style={{ position: "absolute", inset: 0, width: `${(d.in_office_hours / max) * 100}%`, background: "#E5E7EB", borderRadius: 4 }} />
                <div style={{ position: "absolute", top: 0, bottom: 0, width: `${(d.worked_hours / max) * 100}%`, background: "#3B82F6", borderRadius: 4 }} title={`Worked ${hrs(d.worked_hours)}`} />
              </div>
            </div>
            <div style={{ width: 130, fontSize: 12, textAlign: "right" }}>
              <span style={{ color: "#3B82F6", fontWeight: 600 }}>{hrs(d.worked_hours)}</span>
              <span style={{ color: "#9CA3AF" }}> / {hrs(d.in_office_hours)}</span>
            </div>
          </div>
        ))}
        <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
          <span style={{ color: "#3B82F6" }}>■</span> worked (on tasks) &nbsp; <span style={{ color: "#9CA3AF" }}>■</span> in office
        </div>
      </div>
    );
  };

  const renderScorecard = () => {
    if (!summary) return null;
    const t = summary.totals;
    const tiles = [
      { label: "Worked (on tasks)", value: hrs(t.worked_hours), color: "#3B82F6" },
      { label: "In office", value: hrs(t.in_office_hours), color: "#6B7280" },
      { label: "Tasks touched", value: t.tasks_touched, color: "#8B5CF6" },
      { label: "Tasks completed", value: t.tasks_completed, color: "#10B981" },
      { label: "Interruptions", value: t.interruptions, color: "#F59E0B" },
    ];
    return (
      <div>
        <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
          {tiles.map((x) => (
            <div key={x.label} className="p-3" style={{ minWidth: 150, borderRadius: 10, border: "1px solid #eee", borderTop: `3px solid ${x.color}` }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: x.color }}>{x.value}</div>
              <div style={{ fontSize: 13, color: "#666" }}>{x.label}</div>
            </div>
          ))}
        </div>
        {renderBars()}
      </div>
    );
  };

  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Team Report</h5>
          <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: 10 }}>
            <Button label="Today" className={tab === "today" ? "p-button-sm" : "p-button-sm p-button-outlined"} onClick={() => setTab("today")} />
            <Button label="Week" className={tab === "week" ? "p-button-sm" : "p-button-sm p-button-outlined"} onClick={() => setTab("week")} />
            <Button label="Month" className={tab === "month" ? "p-button-sm" : "p-button-sm p-button-outlined"} onClick={() => setTab("month")} />
            <Calendar value={date} onChange={(e) => setDate(e.value)} showIcon className="ml-2" view={tab === "month" ? "month" : "date"} dateFormat={tab === "month" ? "MM yy" : "dd/mm/yy"} />
            {tab !== "today" && (
              <Dropdown value={developer} options={developerOptions} onChange={(e) => setDeveloper(e.value)} placeholder="Select Developer" filter style={{ minWidth: 220 }} />
            )}
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card p-3">
            {loading ? (
              <div className="text-center p-5">
                <ProgressSpinner style={{ width: 40, height: 40 }} />
              </div>
            ) : tab === "today" ? (
              renderToday()
            ) : tab === "week" ? (
              renderBars()
            ) : (
              renderScorecard()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamReport;
